import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Grid } from "@mui/material";
import { ListActiveFacultiesFieldsAPI } from "api/faculty/faculty.api";
import { ListFacultyGroupsAPI } from "api/facultyGroups/facultyGroups.api";
import { CreateStaffsAPI, UpdateStaffsAPI } from "api/staffs/staffs.api";
import { Modal } from "components/Modal";
import SelectField from "components/Select";
import { STAFF_ROLE_OPTIONS } from "constants/options";
import { handleError } from "functions/apiHandleError";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { schema } from "schema/staffs/createUpdateRoleModal";
import useStore from "store";
import classes from "./style.module.scss";

const isGeneralAdminRole = (role) => ["general_admin", "superuser"].includes(role);

export const CreateUpdateRoleModal = ({
  open,
  onClose,
  selectedUser,
  defaultData,
  reload,
  setReload,
  closeOwnModal,
  setCloseOwnModal,
  onSuccess,
}) => {
  const { storeSetLoading } = useStore();

  const { handleSubmit, reset, setError, setValue, watch, control } = useForm({
    mode: "onChange",
    resolver: zodResolver(schema),
    defaultValues: {
      role: "",
      faculties: "",
      fields: "",
    },
  });

  const [loading, setLoading] = useState(false);
  const [facultiesData, setFacultiesData] = useState([]);
  const [fieldsData, setFieldsData] = useState([]);

  const selectedRole = watch("role");
  const selectedFaculty = watch("faculties");

  const showFaculties = selectedRole && !isGeneralAdminRole(selectedRole);

  const showFields = showFaculties && selectedRole !== "faculty_director";

  const getFacultiesData = async () => {
    storeSetLoading(true);

    try {
      const response = await ListActiveFacultiesFieldsAPI();

      setFacultiesData(
        response?.map((item) => ({
          value: String(item.id),
          label: item.fa_name,
        })) ?? []
      );
    } catch (err) {
      handleError({ err });
    } finally {
      storeSetLoading(false);
    }
  };

  const getFieldOptionsData = async (faculty) => {
    storeSetLoading(true);

    try {
      const response = await ListFacultyGroupsAPI({
        page: 1,
        page_size: 100,
        filters: { faculty },
      });

      setFieldsData(
        response?.results?.map((item) => ({
          value: String(item.id),
          label: item.name,
        })) ?? []
      );
    } catch (err) {
      handleError({ err });
    } finally {
      storeSetLoading(false);
    }
  };

  const handleCloseModal = () => {
    reset({
      role: "",
      faculties: "",
      fields: "",
    });

    onClose?.();
  };

  const getRoleLabel = (roleValue) => {
    return STAFF_ROLE_OPTIONS?.find((item) => item.value === roleValue)?.label ?? roleValue ?? "-";
  };

  const getFacultyLabel = (facultyValue) => {
    return facultiesData?.find((item) => String(item.value) === String(facultyValue))?.label ?? "-";
  };

  const getFieldLabel = (fieldValue) => {
    return fieldsData?.find((item) => String(item.value) === String(fieldValue))?.label ?? "-";
  };

  const makeTableRow = ({ response, payload }) => {
    return {
      ...(defaultData ?? {}),
      ...(response ?? {}),

      id: response?.id ?? defaultData?.id,

      role: response?.role ?? payload.role,
      role_display: response?.role_display ?? getRoleLabel(payload.role),

      faculties: response?.faculties ?? payload.faculties,
      faculties_display:
        response?.faculties_display ??
        (payload.faculties ? getFacultyLabel(payload.faculties) : "-"),

      fields: response?.fields ?? payload.fields,
      fields_display:
        response?.fields_display ?? (payload.fields ? getFieldLabel(payload.fields) : "-"),
    };
  };

  const handleSubmitForm = async (data) => {
    const payload = isGeneralAdminRole(data.role)
      ? {
          ...data,
          role: "superuser",
          faculties: null,
          fields: null,
        }
      : data.role === "faculty_director"
      ? {
          ...data,
          fields: null,
        }
      : data;

    setLoading(true);

    try {
      let response;

      if (defaultData) {
        response = await UpdateStaffsAPI({
          pk: defaultData.id,
          data: payload,
        });
      } else {
        response = await CreateStaffsAPI({
          user: selectedUser,
          role: payload.role,
          faculties: payload.faculties,
          fields: payload.fields,
        });
      }

      toast.success("با موفقیت انجام شد");

      const tableRow = makeTableRow({
        response,
        payload,
      });

      await onSuccess?.(tableRow);

      handleCloseModal();
    } catch (err) {
      handleError({ err, setError });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      getFacultiesData();
    }
  }, [open]);

  useEffect(() => {
    if (!showFaculties) {
      setValue("faculties", "");
      setValue("fields", "");
      setFieldsData([]);
      return;
    }

    if (!showFields) {
      setValue("fields", "");
      setFieldsData([]);
      return;
    }

    if (selectedFaculty) {
      getFieldOptionsData(selectedFaculty);
    }
  }, [selectedFaculty, showFaculties, showFields, setValue]);

  useEffect(() => {
    if (!open) return;

    if (!defaultData) {
      reset({
        role: "",
        faculties: "",
        fields: "",
      });
      return;
    }

    reset({
      role: defaultData.role ?? "",
      faculties: defaultData.faculties ? String(defaultData.faculties) : "",
      fields: defaultData.fields ? String(defaultData.fields) : "",
    });
  }, [open, defaultData, reset]);

  return (
    <Modal
      open={open}
      title={defaultData ? "ویرایش سمت" : "ایجاد سمت"}
      onClose={handleCloseModal}
      maxWidth="xs"
      footerEnd={
        <>
          <Button
            variant="contained"
            size="xsmall"
            loading={loading}
            color={defaultData ? "primary" : "success"}
            onClick={handleSubmit(handleSubmitForm)}
          >
            ثبت
          </Button>

          <Button variant="contained" color="error" size="xsmall" onClick={handleCloseModal}>
            بستن
          </Button>
        </>
      }
    >
      <Grid
        container
        spacing={3}
        component="form"
        onSubmit={handleSubmit(handleSubmitForm)}
        className={classes.wrapper}
      >
        <Grid item xs={12}>
          <SelectField
            options={STAFF_ROLE_OPTIONS}
            label="سمت"
            required
            control={control}
            name="role"
          />
        </Grid>

        {showFaculties && (
          <Grid item xs={12}>
            <SelectField
              options={facultiesData}
              label="دانشکده"
              required
              control={control}
              name="faculties"
            />
          </Grid>
        )}

        {showFields && (
          <Grid item xs={12}>
            <SelectField
              options={fieldsData}
              label="گروه"
              required
              control={control}
              name="fields"
            />
          </Grid>
        )}
      </Grid>
    </Modal>
  );
};
